<template>
	<div>
		<!-- Select template -->
		<j-base-multi-select-with-flexible-value
			v-model="selectedTemplate"
			:options="templates"
			:allowMultiselect="false"
			:allowLocalSearch="true"
			placeholder="Search template..."
		/>

		<!-- Variables -->
		<div class="mt4">
			<!-- VariablesJson -->
			<div>
				<p class="fs4 fw7 c1 mb1">{{ $t("graphql_variables") }}</p>
				<core-monaco-editor language="json" v-model="variablesJson" :options="editorOptions" />
			</div>
		</div>

		<div class="mt4">
			<j-button v-if="selectedTemplate" :isLoading="isLoading" @click="downloadPdf">Download PDF</j-button>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: { JButton, CoreMonacoEditor, JBaseMultiSelectWithFlexibleValue },

	props: {
		policyId: {
			type: String
		}
	},

	data() {
		return {
			isLoading: false,
			caseId: null,

			// Templates
			templates: [],
			selectedTemplate: null,

			variablesJson: "",
			contentJson: "",

			// Monaco editor options
			editorOptions: {
				autoIndent: true,
				automaticLayout: true,
				autoClosingBrackets: true,
				formatOnPaste: true,
				formatOnType: true,
				tabCompletion: true
			}
		}
	},

	methods: {
		async fetchTemplates(value = "") {
			const query = gql`
				query fetchTemplates($where: templateWhereInput!, $sort: sortInput) {
					templates(where: $where, sort: $sort) {
						totalCount
						list {
							id
							name
							createdAt
							lastModifiedAt
							__typename
						}
					}
				}
			`

			let variables = {
				where: {},
				sort: { fieldName: "createdAt", type: "desc" }
			}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			this.templates = res?.data?.templates?.list
				?.filter(
					item =>
						item?.__typename === "pdfDrawingTemplate" ||
						item?.__typename === "wkhtmltopdfTemplate"
				)
				?.map(item => ({
					name: item.name,
					value: item.id,
					typeName: item.__typename
				}))
		},

		// HACK: Here we are getting caseId by calling cases and filtering based on policy
		async getCaseIdFromPolicy() {
			const query = gql`
				query getCaseId(
					$proposalWhere: proposalWhere
					$caseWhere: caseWhere
					$policyWhere: policyWhereInput
				) {
					cases(where: $caseWhere) {
						list {
							id
							proposals(where: $proposalWhere) {
								id
								policies(where: $policyWhere) {
									issuerNumber
									id
									createdAt
								}
							}
						}
					}
				}
			`

			const variables = {
				caseWhere: { proposal: { policyId_contains: this.policyId } },
				proposalWhere: { policyId_contains: this.policyId },
				policyWhere: { id: this.policyId }
			}
			const res = await api({ query, variables })
			const caseId = res?.data?.cases?.list?.[0]?.id
			const proposalId = res?.data?.cases?.list?.[0]?.proposals?.[0]?.id
			const createdAt =
				res?.data?.cases?.list?.[0]?.proposals?.[0]?.policies[0]?.createdAt

			// Update variables
			this.variablesJson = JSON.stringify({
				caseWhere1: { id: caseId },
				proposalWhere1: { id: proposalId },
				policyWhere1: { id: this.$route.params.id },
				createdAt // for AEG - EC Policy Schedule template, we need to pass createdAt to use asOf to get the first policy number
			})
		},

		async downloadPdf() {
			// Decide if it is PDF file or HTML PDF
			const templateType = this.selectedTemplate?.[0]?.typeName
			let apiKey = "renderPdfDrawingTemplate"
			if (templateType === "wkhtmltopdfTemplate")
				apiKey = "renderWkhtmltopdfTemplate"

			// Query
			const query = `
				query ${apiKey}($templateId: String!, $input: templateRenderParametersInput){
					${apiKey}(templateId: $templateId input: $input) {
						errors
						status
						value
					}
				}
			`

			// Variables
			const variables = {
				templateId: this.selectedTemplate?.[0]?.value,
				input: { name: "data" }
			}

			variables.input.variablesJsonString = this.variablesJson

			// Call
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			// Errors
			if (res?.data?.[apiKey]?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.[apiKey]?.errors?.[0] || [this.$t("errors.general")]
				)
				return
			}

			// Download file
			let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
			let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
			let link = document.createElement("a")
			link.href = window.URL.createObjectURL(blob)
			link.download = `${this.selectedTemplate?.[0]?.name}.pdf`
			link.click()
		}
	},

	mounted() {
		this.fetchTemplates()
		this.getCaseIdFromPolicy()
	}
}
</script>
